import { useState } from 'react'
import cx from 'classnames'
import { useQueryClient } from 'react-query'
import { Modal, useModals } from 'almanac-ui/Modal'
import { Button } from 'components/Button'
import { Button as BlazeButton } from 'blaze-ui/Button/Button'
import { ReactComponent as ArrowLeft } from 'assets/icons/arrowLeft16.svg'
import { useCurrentUserProfile } from 'hooks/queries/users/useCurrentUserProfile'
import client from 'api/client'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

import {
  backgroundColors,
  foregroundColors,
  heroImages,
  identities,
  IdentityKey,
  ImageKey,
  SecretIdentity,
} from './identities'
import { SecretIdentityCard } from './SecretIdentityCard/SecretIdentityCard'
import { Underlay } from './Underlay/Underlay'
import styles from './SecretIdentityModal.module.scss'
import { HeapEventType, trackEventInHeap } from 'utils/heapTracking'
import { LargeCardWithButtons } from './LargeCardWithButtons'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { SecretIdentity as SecretIdentityType } from 'types'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { InterfaceColorSettings } from 'components/Settings/Workspace/InterfaceColorSettings/InterfaceColorSettings'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { LogoSettings } from 'components/Settings/Workspace/LogoSettings/LogoSettings'
import { useOpenBrandVoiceModal } from 'screens/BrandKits/components/BrandVoice/useOpenBrandVoiceModal'
import ArrowSwitchHorizontal from 'blaze-ui/icons/20/ArrowSwitchHorizontal'

const INTERCOM_CHECKLIST_ID = 35972838

export const SecretIdentityModal = () => {
  const [selectedIdentity, setSelectedIdentity] = useState<SecretIdentity>()
  const [imageKey, setImageKey] = useState<ImageKey>()
  const [index, setIndex] = useState(0)
  const profile = useCurrentUserProfile()
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  const { isDesktop, isMobile } = useResponsiveDesign()
  const workspace = useCurrentWorkspace()
  const openAddVoiceModal = useOpenBrandVoiceModal()

  const [foregroundColor, setForegroundColor] = useState<string>('')
  const [backgroundColor, setBackgroundColor] = useState<string>('')

  const { closeModal } = useModals()

  const handleCloseModal = () => {
    try {
      if (isDesktop) {
        window.Intercom('startChecklist', INTERCOM_CHECKLIST_ID)
      }
    } finally {
      closeModal()
    }
  }

  const handleSkip = () => {
    trackEventInHeap(HeapEventType.Onboarding, { action: 'skip-secret-identity' })
    client.trackEvent('Event::SecretIdentitySkipped', {})
    handleCloseModal()
  }

  const handleClickImage = (imageKey: ImageKey, index: number) => {
    setImageKey(imageKey)
    setIndex(index)
  }

  const handleSelectImage = () => {
    setImageKey(imageKey)
    setSelectedIdentity(getRandomIdentity())
    setBackgroundColor(backgroundColors[index % backgroundColors.length])
    setForegroundColor(foregroundColors[(index + 1) % foregroundColors.length])
    if (isMobile) {
      const el = document.getElementById('secret-identity-modal-body')
      el?.scroll(0, 0)
    }
  }

  const handleOpenBrandVoice = async () => {
    await handleSubmit()
    openAddVoiceModal()
  }

  const handleSubmit = async () => {
    if (!workspaceId || !profile.data || !selectedIdentity || !imageKey) return

    await client.updateWorkspaceMemberProfile(workspaceId, profile.data?.id, {
      secret_identity: {
        identity_key: selectedIdentity.key,
        hero_image_key: imageKey,
        foreground_color: foregroundColor,
        background_color: backgroundColor,
      },
    })

    await queryClient.invalidateQueries(['user-profile'])

    handleCloseModal()
  }

  const randomizeIdentity = () => {
    setSelectedIdentity(getNextIdentity())
    const newBackgroundColor: string = backgroundColors.filter((color) => color !== backgroundColor)[
      Math.floor(Math.random() * (backgroundColors.length - 1))
    ]
    const newForegroundColor: string = foregroundColors.filter((color) => color !== newBackgroundColor)[
      Math.floor(Math.random() * (foregroundColors.length - 1))
    ]

    setBackgroundColor(newBackgroundColor)
    setForegroundColor(newForegroundColor)
  }

  const userName = profile.data?.first_name || ''

  const getRandomIdentity = () => {
    const keys = Object.keys(identities).filter(
      (key) => key !== (profile.data?.secret_identity as SecretIdentityType).identity_key,
    )
    const randomKey = keys[Math.floor(Math.random() * keys.length)]
    return identities[randomKey as IdentityKey]
  }

  const getNextIdentity = () => {
    const keys = Object.keys(identities)
    const currentIndex = keys.findIndex((key) => key === selectedIdentity?.key)
    const nextIndex = (currentIndex + 1) % keys.length
    const nextKey = keys[nextIndex]

    return identities[nextKey as IdentityKey]
  }

  return (
    <Modal.Root
      size="lg"
      isKeyboardDismissDisabled
      onPressOutside={handleSkip}
      bodyClassName={styles.modalBody}
      wrapperClassName={styles.modalWrapper}
    >
      <div id="secret-identity-modal-body" className={cx(styles.modalBody, { [styles.step2]: !!selectedIdentity })}>
        <div className={cx(styles.step, { [styles.active]: !!selectedIdentity })}>
          <div className={styles.mobileWelcomeContainerOuter}>
            <div className={styles.mobileWelcomeContainer}>
              <div className={styles.mobileHeader}>
                <div className={styles.mobileCardWrapper}>
                  {selectedIdentity && (
                    <LargeCardWithButtons
                      secretIdentity={{
                        identity_key: selectedIdentity.key,
                        hero_image_key: imageKey as ImageKey,
                        background_color: backgroundColor,
                        foreground_color: foregroundColor,
                      }}
                    />
                  )}
                  <div className={styles.headerContainer}>
                    <Heading level={4} color="var(--neutral-90)">
                      Welcome to Blaze, {userName}!
                    </Heading>
                    <Text variant="secondary" color="var(--dark-60)">
                      Share your Blaze membership card with the world.
                    </Text>
                  </div>
                </div>
              </div>

              <div className={styles.divider} />
              {workspace && <InterfaceColorSettings workspace={workspace} />}
              <div className={styles.divider} />
              {workspace && <LogoSettings workspace={workspace} />}
              <div className={styles.selectContainer}>
                <BlazeButton variant="primary" size="lg" onPress={handleSubmit}>
                  Jump into Blaze
                </BlazeButton>
                <BlazeButton variant="primary" size="lg" onPress={handleOpenBrandVoice}>
                  Create your first Brand Voice
                </BlazeButton>
              </div>
            </div>
          </div>
          <Underlay fill="var(--default-bg)">
            <div className={styles.modalInner}>
              {selectedIdentity && (
                <LargeCardWithButtons
                  secretIdentity={{
                    identity_key: selectedIdentity.key,
                    hero_image_key: imageKey as ImageKey,
                    background_color: backgroundColor,
                    foreground_color: foregroundColor,
                  }}
                >
                  <BlazeButton
                    size="md"
                    variant="secondary"
                    frontIcon={ArrowSwitchHorizontal}
                    onPress={randomizeIdentity}
                  >
                    Change
                  </BlazeButton>
                </LargeCardWithButtons>
              )}
              <div className={styles.buttonContainer}>
                <Button
                  size="lg"
                  variant="secondaryGrey"
                  className={cx(styles.backButton, styles.whiteButton)}
                  frontIcon={ArrowLeft}
                  onPress={() => setSelectedIdentity(undefined)}
                />
              </div>
              <div className={styles.welcomeContainer}>
                <div className={styles.welcomeContainerInner}>
                  <div className={styles.section}>
                    <Heading level={3} color="var(--neutral-90)">
                      Welcome to Blaze, {userName}!
                    </Heading>
                    <Text variant="secondary" color="var(--dark-60)">
                      Share your Blaze membership card with the world.
                    </Text>
                  </div>
                  <div className={styles.divider} />
                  {workspace && <InterfaceColorSettings workspace={workspace} />}
                  <div className={styles.divider} />
                  {workspace && <LogoSettings workspace={workspace} />}
                </div>
                <div className={styles.desktopButtonRow}>
                  <BlazeButton variant="primary" size="lg" onPress={handleSubmit}>
                    Jump into Blaze
                  </BlazeButton>
                </div>
              </div>
            </div>
          </Underlay>
        </div>
        <div className={cx(styles.step, styles.step1, { [styles.active]: !selectedIdentity })}>
          <div className={styles.modalHeaderContainer}>
            <div className={styles.modalHeaderTitle}>
              <Heading level={3}>Choose your Blaze avatar</Heading>
            </div>
            <span className={styles.modalSubheader}>You’ll see your marketing superpower on the next screen!</span>
          </div>
          <div className={styles.grid}>
            {Object.keys(heroImages).map((key, i) => (
              <SecretIdentityCard
                key={key}
                imageKey={key as ImageKey}
                isSelected={imageKey === key}
                onSelect={() => handleClickImage(key as ImageKey, i)}
              />
            ))}
          </div>
          <div className={styles.selectContainer}>
            {!isMobile && (
              <BlazeButton variant="tertiary" size="lg" onPress={handleSkip}>
                Cancel
              </BlazeButton>
            )}
            <BlazeButton
              variant="primary"
              fullWidth={isMobile}
              isDisabled={!imageKey}
              size="lg"
              onPress={handleSelectImage}
            >
              Let's Go!
            </BlazeButton>
            {!selectedIdentity && isMobile && (
              <Button variant="tertiary" onPress={handleSkip} size="lg" className={styles.skipButton}>
                Skip
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal.Root>
  )
}
